import React, { useEffect, useState } from 'react';
import { Button, Popover, Switch, Tooltip, type ButtonProps } from 'antd';
import { useRecoilState } from 'recoil';
import { useTranslation } from 'react-i18next';
import {
  AimOutlined,
  ApartmentOutlined,
  AppstoreOutlined,
  BulbOutlined,
  CalendarOutlined,
  CheckCircleOutlined,
  CrownOutlined,
  DragOutlined,
  EditOutlined,
  FileTextOutlined,
  FilterOutlined,
  NodeExpandOutlined,
  PlusOutlined,
  SearchOutlined,
  SelectOutlined,
  SwapOutlined,
  TeamOutlined,
  UnorderedListOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { apv2RowContextHintEnabledAtom, apv2TipsSeenAtom } from '@/states/apv2WorkspaceView';
import { aiAgentApi } from '@/services/knowledge/ai-agent-api';
import type { AiHubApEffectiveRule } from '@/types/ai-agent';

// #region Apv2TipsButton

/** Trang đang dùng bóng đèn — mỗi trang có mẹo + quy tắc hiển thị riêng. */
export type Apv2TipsVariant = 'workspace' | 'tongQuan' | 'home' | 'assistant';

type TipItem = { icon: React.ReactNode; text: string };
type TipSection = { title: string; items: TipItem[] };

type Props = {
  variant: Apv2TipsVariant;
  buttonClassName?: string;
  buttonSize?: ButtonProps['size'];
};

function useTipSections(variant: Apv2TipsVariant): TipSection[] {
  const { t } = useTranslation();
  const k = (key: string, fallback: string) => t(`bsc.actionplan.v2.tips.${variant}.${key}`, fallback);

  const rightClick: TipItem = {
    icon: <SelectOutlined />,
    text: k(
      'rightclick',
      'Nhấn chuột phải vào dòng mục tiêu / công việc để mở tuỳ chọn: Chi tiết, Báo cáo, Thêm việc con, Sao chép / Dán, Xoá…'
    ),
  };
  const editSave: TipItem = {
    icon: <EditOutlined />,
    text: k('edit', 'Bấm vào ô để sửa trực tiếp, sửa xong nhấn «Lưu».'),
  };
  const search: TipItem = {
    icon: <SearchOutlined />,
    text: k('search', 'Kính lúp để tìm nhanh công việc và mở chi tiết.'),
  };

  if (variant === 'workspace') {
    return [
      {
        title: k('section.actions', 'Thao tác'),
        items: [
          rightClick,
          { icon: <NodeExpandOutlined />, text: k('expand', 'Bấm vào dòng mục tiêu để mở / thu gọn công việc bên dưới.') },
          editSave,
          { icon: <DragOutlined />, text: k('drag', 'Kéo thả dòng để đổi vị trí công việc.') },
          { icon: <FilterOutlined />, text: k('filter', 'Nút Lọc để chọn mục tiêu / khoảng thời gian.') },
          search,
          {
            icon: <AppstoreOutlined />,
            text: k('views', 'Đổi kiểu xem bằng thanh tab bên trái: Overview, Bảng, Thời gian, Kanban…'),
          },
        ],
      },
      {
        title: k('section.rules', 'Quy tắc hiển thị'),
        items: [
          { icon: <AimOutlined />, text: k('rule.sheet', 'Mỗi mục tiêu là một bảng riêng, chỉ hiện mục tiêu bạn được xem.') },
          {
            icon: <TeamOutlined />,
            text: k(
              'rule.scope',
              'Hiện công việc do người trong phạm vi đang chọn (bộ phận / nhân viên) phụ trách, kèm toàn bộ việc con.'
            ),
          },
          {
            icon: <CalendarOutlined />,
            text: k(
              'rule.date',
              'Mặc định lọc 30 ngày gần nhất; «Xoá bộ lọc» để xem tất cả. Khoảng ngày dùng chung với trang Việc của tôi.'
            ),
          },
          { icon: <NodeExpandOutlined />, text: k('rule.lazy', 'Cây tải theo cấp: mở dòng nào mới tải việc con của dòng đó.') },
        ],
      },
      {
        title: k('section.diff', 'Khác với Tổng quan công việc'),
        items: [
          {
            icon: <SwapOutlined />,
            text: k(
              'diff',
              'Workspace nhóm theo mục tiêu, có cột động, kéo thả vị trí và nhiều kiểu xem. Tổng quan gộp mọi việc vào một bảng (gốc là dự án), không cột động, không kéo thả. Dùng công tắc «Tổng quan / Workspace» ở đầu thanh công cụ để chuyển qua lại.'
            ),
          },
        ],
      },
    ];
  }

  if (variant === 'assistant') {
    return [
      {
        title: k('section.actions', 'Trợ lý làm được gì'),
        items: [
          {
            icon: <SearchOutlined />,
            text: k('read', 'Xem việc quá hạn / hôm nay / sắp tới, việc gốc, số việc theo trạng thái của phòng hoặc nhân viên.'),
          },
          {
            icon: <PlusOutlined />,
            text: k('create', 'Tạo việc, chia việc con, sửa tên / hạn / mô tả: trợ lý hiện thẻ, chỉ ghi khi bạn bấm «Xác nhận».'),
          },
          {
            icon: <CheckCircleOutlined />,
            text: k('rule.common', 'Hạn không trước hôm nay; việc con nằm trong khoảng ngày của việc cha; mục tiêu phải thuộc phạm vi của bạn.'),
          },
        ],
      },
      {
        title: k('section.rules', 'Quyền theo cơ cấu'),
        items: [
          {
            icon: <UserOutlined />,
            text: k('rule.staff', 'Nhân viên: chỉ tạo việc cho chính mình, chỉ sửa việc mình phụ trách.'),
          },
          {
            icon: <TeamOutlined />,
            text: k(
              'rule.manager',
              'Quản lý: tạo / sửa việc cho mình và nhân viên trong phòng (kèm phòng con), chia thêm việc con cho các việc đó.'
            ),
          },
          { icon: <CrownOutlined />, text: k('rule.admin', 'Admin: toàn quyền trong đơn vị.') },
          {
            icon: <ApartmentOutlined />,
            text: k('rule.outside', 'Ngoài phạm vi cơ cấu: trợ lý chỉ giải thích, không hiện thẻ ghi.'),
          },
        ],
      },
    ];
  }

  if (variant === 'tongQuan') {
    return [
      {
        title: k('section.actions', 'Thao tác'),
        items: [
          rightClick,
          { icon: <NodeExpandOutlined />, text: k('expand', 'Bấm mũi tên để mở việc con — mở tới đâu tải tới đó.') },
          editSave,
          { icon: <PlusOutlined />, text: k('create', '«Tạo công việc» thêm một việc gốc mới lên đầu bảng.') },
          {
            icon: <FilterOutlined />,
            text: k('filter', 'Nút Lọc chọn mục tiêu / ẩn việc đã xong 100%; khoảng ngày chọn ngay trên bảng.'),
          },
          search,
        ],
      },
      {
        title: k('section.rules', 'Quy tắc hiển thị'),
        items: [
          {
            icon: <UnorderedListOutlined />,
            text: k(
              'rule.groups',
              'Gộp 3 nhóm (cột Loại): Phụ trách — người trong phạm vi phụ trách; Được giao — phụ trách việc thuộc mục tiêu ngoài quyền xem; Đã giao — người trong phạm vi tạo, người khác phụ trách.'
            ),
          },
          {
            icon: <TeamOutlined />,
            text: k('rule.scope', 'Chọn bộ phận: theo quản lý / admin bộ phận. Chọn nhân viên: chỉ việc của người đó.'),
          },
          {
            icon: <CalendarOutlined />,
            text: k('rule.order', 'Gốc là dự án, sắp theo ngày cập nhật mới nhất; việc con theo vị trí.'),
          },
        ],
      },
      {
        title: k('section.diff', 'Khác với Workspace'),
        items: [
          {
            icon: <SwapOutlined />,
            text: k(
              'diff',
              'Tổng quan xem chung mọi việc (cả việc được giao / đã giao) trong một bảng, không chia theo mục tiêu, không cột động, không kéo thả. Muốn sắp xếp vị trí, thêm cột hay xem Kanban → bấm «Workspace» ở đầu thanh công cụ.'
            ),
          },
        ],
      },
    ];
  }

  return [
    {
      title: k('section.actions', 'Thao tác'),
      items: [
        { icon: <SwapOutlined />, text: k('mode', 'Công tắc Cá nhân / Quản lý cạnh tiêu đề để đổi góc nhìn.') },
        {
          icon: <UnorderedListOutlined />,
          text: k('tabs', 'Thanh chọn: Danh sách công việc, Công việc đã giao, Mục tiêu; «Tổng quan công việc» mở trang tổng quan.'),
        },
        { icon: <PlusOutlined />, text: k('create', '«Tạo nhanh» để thêm công việc mới.') },
        { icon: <FileTextOutlined />, text: k('report', '«Báo cáo nhanh» để cập nhật tiến độ nhiều việc cùng lúc.') },
        {
          icon: <FilterOutlined />,
          text: k('filter', 'Nút Lọc: vai trò (Phụ trách / Hỗ trợ), khoảng ngày, mục tiêu.'),
        },
        search,
      ],
    },
    {
      title: k('section.rules', 'Quy tắc hiển thị'),
      items: [
        {
          icon: <CalendarOutlined />,
          text: k('rule.priority', 'Danh sách công việc ưu tiên việc Quá hạn, Hôm nay, Sắp tới.'),
        },
        {
          icon: <AimOutlined />,
          text: k('rule.stats', 'Thẻ thống kê tính trên toàn bộ việc, không đổi theo bộ lọc mục tiêu.'),
        },
        {
          icon: <FilterOutlined />,
          text: k('rule.filter', 'Lọc mục tiêu áp dụng ngay trên trang, không tải lại. Khoảng ngày mặc định 30 ngày, dùng chung với Workspace.'),
        },
      ],
    },
  ];
}

/** Bóng đèn «Mẹo thao tác»: mẹo + quy tắc hiển thị theo trang; trang có bảng thêm công tắc gợi ý chuột phải. */
export const Apv2TipsButton: React.FC<Props> = ({ variant, buttonClassName, buttonSize }) => {
  const { t } = useTranslation();
  const [tipsSeen, setTipsSeen] = useRecoilState(apv2TipsSeenAtom);
  const [hintEnabled, setHintEnabled] = useRecoilState(apv2RowContextHintEnabledAtom);
  const [open, setOpen] = useState(false);
  const baseSections = useTipSections(variant);
  // Trợ lý: rule đang áp cho công ty (mã R01…, theo Cài đặt AI Agent) thay cho danh sách cố định
  const [apRules, setApRules] = useState<AiHubApEffectiveRule[] | null>(null);
  useEffect(() => {
    if (variant !== 'assistant' || !open || apRules) return;
    void aiAgentApi
      .actionPlanRules()
      .then((res) => setApRules(res?.success ? res.data.effective.filter((r) => r.batTat) : []))
      .catch(() => setApRules([]));
  }, [apRules, open, variant]);
  const sections: TipSection[] =
    variant === 'assistant' && apRules?.length
      ? [
          baseSections[0],
          {
            title: t('bsc.actionplan.v2.tips.assistant.section.applied', 'Rule đang áp dụng'),
            items: apRules.map((r) => ({
              icon: <span style={{ fontFamily: 'monospace', fontSize: 10 }}>{r.code.replace('+', '')}</span>,
              text: r.noiDung,
            })),
          },
        ]
      : baseSections;
  const hasTable = variant !== 'home' && variant !== 'assistant';
  const label = t('bsc.actionplan.v2.tips.title', 'Mẹo thao tác');

  return (
    <Popover
      trigger="click"
      placement="bottomRight"
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (next && !tipsSeen) setTipsSeen(true);
      }}
      title={
        <span className="inline-flex items-center gap-1.5 text-[14px] font-semibold text-[#1D2939]">
          <BulbOutlined className="text-[#F79009]" />
          {label}
        </span>
      }
      content={
        <div
          className="flex max-h-[min(70vh,560px)] w-[360px] max-w-[calc(100vw-48px)] flex-col gap-3 overflow-y-auto pr-1"
          data-testid="apv2-tips-popover"
        >
          {sections.map((section) => (
            <section key={section.title} className="flex flex-col gap-2">
              <div className="text-[11px] font-semibold uppercase tracking-[0.06em] text-[#667085]">{section.title}</div>
              <ul className="m-0 flex list-none flex-col gap-2 p-0">
                {section.items.map((tip, idx) => (
                  <li key={idx} className="flex items-start gap-2.5 text-[13px] leading-5 text-[#344054]">
                    <span
                      className="mt-0.5 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-[#EAF2FF] text-[13px] text-[#175CD3]"
                      aria-hidden
                    >
                      {tip.icon}
                    </span>
                    <span>{tip.text}</span>
                  </li>
                ))}
              </ul>
            </section>
          ))}
          {hasTable ? (
            <label className="flex cursor-pointer items-center justify-between gap-3 border-t border-[#F2F4F7] pt-3 text-[13px] text-[#475467]">
              {t('bsc.actionplan.v2.tips.hovertoggle', 'Hiện gợi ý khi rê chuột vào dòng')}
              <Switch size="small" checked={hintEnabled} onChange={setHintEnabled} />
            </label>
          ) : null}
        </div>
      }
    >
      <Tooltip title={open ? undefined : label}>
        <span className="relative inline-flex">
          <Button
            className={buttonClassName}
            size={buttonSize}
            icon={<BulbOutlined />}
            aria-label={label}
            data-testid="apv2-tips-btn"
          />
          {!tipsSeen ? (
            <span className="pointer-events-none absolute right-0.5 top-0.5 flex h-2 w-2" aria-hidden>
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#F79009] opacity-75 motion-reduce:hidden" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-[#F79009]" />
            </span>
          ) : null}
        </span>
      </Tooltip>
    </Popover>
  );
};

export default Apv2TipsButton;

// #endregion Apv2TipsButton
